import { useCallback, useEffect, useMemo, useState, type FocusEvent, type ReactNode } from 'react';
import { Box, IconButton, MenuItem, Pagination, Select, Stack, Typography, useMediaQuery, useTheme } from '@mui/material';
import { ChevronLeftOutlined, ChevronRightOutlined } from '@mui/icons-material';
import {
  DataGrid,
  useGridApiRef,
  type DataGridProps,
  type GridColDef,
  type GridColumnVisibilityModel,
  type GridPaginationModel,
  type GridRowParams,
  type GridRowId,
  type GridSortModel,
  type GridApi,
  type GridValidRowModel,
} from '@mui/x-data-grid';
import { CommonFilterBar, type FilterBarSearchProps, type FilterDefinition } from './CommonFilterBar';
import { ResourceState } from './ResourceState';
import { TableCellFocusContext, TableGridFocusContext, TableTextCell } from './TableTextCell';
import { designTokens } from '../tokens';

export type ResourceTableState =
  | { kind: 'loading'; label?: ReactNode }
  | { kind: 'error'; message: ReactNode; title?: ReactNode; retryLabel?: ReactNode; onRetry?: () => void; action?: ReactNode }
  | { kind: 'empty'; title?: ReactNode; description?: ReactNode; action?: ReactNode }
  | { kind: 'ready' };

export type ResourceTableProps<Row extends GridValidRowModel> = FilterBarSearchProps & {
  rows: readonly Row[];
  totalCount: number;
  columns: readonly GridColDef<Row>[];
  ariaLabel?: string;
  filters?: FilterDefinition[];
  trailing?: ReactNode;
  onRowClick?: (params: GridRowParams<Row>) => void;
  selection?: { ids: readonly GridRowId[]; onChange: (ids: GridRowId[]) => void };
  rowHeight?: number;
  /** 기본 텍스트 셀의 최대 줄 수입니다. 두 줄은 기본 행 높이도 함께 늘립니다. */
  textLines?: 1 | 2;
  /** @deprecated 페이지 없이 전체를 표시하려면 rowDisplayMode="all"을 사용합니다. */
  hideFooter?: boolean;
  rowDisplayMode?: 'paginated' | 'all';
  showTotalCount?: boolean;
  stickyColumnCount?: number;
  columnVisibilityModel?: GridColumnVisibilityModel;
  /** 최초 데이터 렌더링 시 헤더와 표시된 셀에 열 폭을 맞춥니다. 기본값은 true입니다. */
  autosizeColumns?: boolean;
  /** 카드 안에 놓인 표는 별도의 모서리 장식을 사용하지 않습니다. */
  embedded?: boolean;
  state?: ResourceTableState;
};

const pageSizeOptions = [25, 50, 100] as const;
const defaultFlexibleColumnMinWidth = 160;
const defaultColumnMinWidth = 120;
const defaultAutosizeColumnMinWidth = 50;
const autosizeOptions = { includeHeaders: true, includeOutliers: true, expand: false };
const stickyColumnClassPrefix = 'ResourceTable-stickyColumn-';
const actionColumnClassName = 'ResourceTable-actionColumn';
const numericColumnClassName = 'ResourceTable-numericColumn';

function appendClassName<Params>(
  existing: string | ((params: Params) => string) | undefined,
  className: string,
) {
  if (typeof existing === 'function') return (params: Params) => `${existing(params)} ${className}`.trim();
  return [existing, className].filter(Boolean).join(' ');
}

function ResourceDataGrid<Row extends GridValidRowModel>({
  keepColumnsMounted,
  stickyFields,
  initialAutosize,
  onInitialAutosize,
  ...props
}: DataGridProps<Row> & {
  keepColumnsMounted: boolean;
  stickyFields: readonly string[];
  initialAutosize: boolean;
  onInitialAutosize: (widths: Record<string, number>) => void;
}) {
  const apiRef = useGridApiRef();
  const hasRows = Boolean(props.rows?.length);

  useEffect(() => {
    // CSS 고정 열은 스크롤 범위 밖에서도 DOM에 남아야 합니다. 행 가상화는 유지합니다.
    // 표와 함께 마운트해 로딩 복구와 StrictMode 재마운트에도 설정을 적용합니다.
    apiRef.current?.unstable_setColumnVirtualization(!keepColumnsMounted);
    if (!keepColumnsMounted) return;

    // MUI의 열 너비 자동 맞춤도 열 가상화를 다시 켜므로 고정 열 정책을 유지합니다.
    return apiRef.current?.subscribeEvent('stateChange', () => {
      apiRef.current?.unstable_setColumnVirtualization(false);
    });
  }, [apiRef, keepColumnsMounted]);

  useEffect(() => {
    if (!initialAutosize || !hasRows) return;
    const api = apiRef.current;
    if (!api) return;
    let frame: number | undefined;
    let disposed = false;
    let started = false;
    const schedule = () => {
      if (disposed || started || frame !== undefined) return;
      frame = requestAnimationFrame(async () => {
        frame = undefined;
        // 빈 표 마운트로 최초 맞춤을 소비하지 않고 실제 행과 viewport가 준비될 때까지 기다립니다.
        if (!api.getRootDimensions()?.viewportInnerSize.width
          || !api.rootElementRef.current?.querySelector('[role="row"][data-id]')) return;
        started = true;
        const visibleColumns: ReturnType<GridApi['getVisibleColumns']> = api.getVisibleColumns();
        await api.autosizeColumns({
          ...autosizeOptions,
          columns: visibleColumns.filter((column) => column.field !== '__check__').map((column) => column.field),
        });
        if (!disposed) {
          const sizedColumns: ReturnType<GridApi['getVisibleColumns']> = api.getVisibleColumns();
          onInitialAutosize(Object.fromEntries(sizedColumns.map((column) => [column.field, column.computedWidth])));
        }
      });
    };
    const unsubscribeState = api.subscribeEvent('stateChange', schedule);
    const unsubscribeRows = api.subscribeEvent('renderedRowsIntervalChange', schedule);
    schedule();
    return () => {
      disposed = true;
      if (frame !== undefined) cancelAnimationFrame(frame);
      unsubscribeState();
      unsubscribeRows();
    };
  }, [apiRef, hasRows, initialAutosize, onInitialAutosize]);

  const revealFocusedCell = (event: FocusEvent<HTMLDivElement>) => {
    props.slotProps?.root?.onFocusCapture?.(event);
    if (!(event.target instanceof HTMLElement)) return;
    const cell = event.target.closest<HTMLElement>('[role="gridcell"], [role="columnheader"]');
    if (!cell) return;
    // MUI는 표 안의 스크롤만 조절합니다. 삽입된 표의 초점이 페이지 밖으로 벗어나면 함께 드러냅니다.
    const bounds = cell.getBoundingClientRect();
    if (bounds.top < 0 || bounds.bottom > window.innerHeight) {
      cell.scrollIntoView({ block: 'nearest', inline: 'nearest', behavior: 'auto' });
    }
    const api = apiRef.current;
    if (!api || !keepColumnsMounted) return;
    const field = cell?.dataset.field;
    if (!field || stickyFields.includes(field)) return;

    // MUI는 CSS 고정 열의 폭을 모릅니다. 방향키로 이동한 셀을 고정 영역 옆에 드러냅니다.
    // 실제 계산 너비를 사용하므로 숨긴 열과 사용자가 조절한 열 너비도 반영합니다.
    const visibleColumns: ReturnType<GridApi['getVisibleColumns']> = api.getVisibleColumns();
    const columnIndex = visibleColumns.findIndex((column) => column.field === field);
    const viewportWidth = api.getRootDimensions()?.viewportInnerSize.width;
    if (columnIndex < 0 || !viewportWidth) return;
    const stickyWidth = visibleColumns.reduce((width, column) => width + (stickyFields.includes(column.field) ? column.computedWidth : 0), 0);
    const offset = visibleColumns.slice(0, columnIndex).reduce((width, column) => width + column.computedWidth, 0);
    const columnWidth = visibleColumns[columnIndex].computedWidth;
    const scrollLeft = api.getScrollPosition().left;
    const availableWidth = Math.max(0, viewportWidth - stickyWidth);
    if (offset - scrollLeft < stickyWidth || columnWidth > availableWidth) {
      api.scroll({ left: Math.max(0, offset - stickyWidth) });
    } else if (offset + columnWidth - scrollLeft > viewportWidth) {
      api.scroll({ left: Math.max(0, offset + columnWidth - viewportWidth) });
    }
  };

  return <DataGrid
    {...props}
    apiRef={apiRef}
    autosizeOptions={autosizeOptions}
    slotProps={{ ...props.slotProps, root: { ...props.slotProps?.root, onFocusCapture: revealFocusedCell } }}
  />;
}

export function ResourceTable<Row extends GridValidRowModel>(props: ResourceTableProps<Row>) {
  const {
    rows,
    totalCount,
    columns,
    query,
    ariaLabel,
    filters,
    trailing,
    onRowClick,
    selection,
    rowHeight = designTokens.layout.resourceRowHeight,
    textLines = 1,
    hideFooter = false,
    rowDisplayMode = 'paginated',
    showTotalCount = true,
    stickyColumnCount = 1,
    columnVisibilityModel,
    autosizeColumns = true,
    embedded = false,
    state = { kind: 'ready' },
  } = props;
  const searchProps: FilterBarSearchProps = props.searchable === false
    ? { searchable: false }
    : {
      searchable: true,
      query: props.query,
      onQueryChange: props.onQueryChange,
      searchPlaceholder: props.searchPlaceholder,
      searchLabel: props.searchLabel,
    };
  const theme = useTheme();
  const compact = useMediaQuery(theme.breakpoints.down('sm'));
  const coarsePointer = useMediaQuery('(pointer: coarse)');
  const baseRowHeight = compact || coarsePointer
    ? designTokens.layout.resourceRowHeightCompact
    : designTokens.layout.resourceRowHeight;
  const textRowHeight = textLines === 2 ? designTokens.layout.resourceRowHeightMultiline : baseRowHeight;
  const resolvedRowHeight = Math.max(rowHeight, baseRowHeight, textRowHeight);
  const [paginationModel, setPaginationModel] = useState<GridPaginationModel>({ page: 0, pageSize: 25 });
  const [sortModel, setSortModel] = useState<GridSortModel>([]);
  const [gridHasFocus, setGridHasFocus] = useState(false);
  // Grid는 로딩·빈 결과·오류에서 unmount됩니다. 표가 살아 있는 동안 초기/수동 폭을 보존합니다.
  const [columnWidths, setColumnWidths] = useState<Record<string, number>>({});
  const [initialAutosizeComplete, setInitialAutosizeComplete] = useState(false);
  const completeInitialAutosize = useCallback((widths: Record<string, number>) => {
    setColumnWidths((current) => ({ ...current, ...widths }));
    setInitialAutosizeComplete(true);
  }, []);
  const pageCount = Math.max(1, Math.ceil(rows.length / paginationModel.pageSize));
  const hasNarrowedResults = rows.length !== totalCount;
  const showAllRows = rowDisplayMode === 'all' || hideFooter;
  const showPagination = !showAllRows;
  const showCollectionMeta = showTotalCount || showPagination;
  const filterValueSignature = (filters ?? []).map((filter) => `${filter.id ?? filter.label}:${filter.value}`).join('\u0000');
  const resolvedStickyColumnCount = Math.max(0, Math.min(stickyColumnCount, columns.length));
  const hasStickyColumns = Boolean(selection) || resolvedStickyColumnCount > 0;

  const { displayColumns, stickyColumnStyles } = useMemo(() => {
    let stickyLeft = selection ? 50 : 0;
    const styles: Record<string, object> = selection ? {
      '& .MuiDataGrid-cellCheckbox, & .MuiDataGrid-columnHeaderCheckbox': { position: 'sticky', left: 0, zIndex: 3, bgcolor: 'inherit' },
      '& .MuiDataGrid-columnHeaderCheckbox': { zIndex: 5 },
    } : {};
    const resolved = columns.map((sourceColumn, index) => {
      const column: GridColDef<Row> = {
        ...sourceColumn,
        ...(sourceColumn.type === 'number' || sourceColumn.type === 'actions' || sourceColumn.field === 'actions'
          ? { align: sourceColumn.align ?? 'right', headerAlign: sourceColumn.headerAlign ?? 'right' }
          : undefined),
      };
      if (sourceColumn.type === 'actions' || sourceColumn.field === 'actions') {
        column.cellClassName = appendClassName(sourceColumn.cellClassName, actionColumnClassName);
        column.headerClassName = appendClassName(sourceColumn.headerClassName, actionColumnClassName);
      }
      if (sourceColumn.type === 'number') {
        column.cellClassName = appendClassName(column.cellClassName, numericColumnClassName);
      }
      if (sourceColumn.renderCell || !sourceColumn.type || ['string', 'number', 'date', 'dateTime'].includes(sourceColumn.type)) {
        column.renderCell = (params) => {
          const value = sourceColumn.renderCell
            ? sourceColumn.renderCell(params)
            : params.formattedValue ?? params.value;
          const content = typeof value === 'string' || typeof value === 'number'
            ? <TableTextCell lines={textLines} align={column.align}>{value}</TableTextCell>
            : value;
          return <TableCellFocusContext.Provider value={params.hasFocus}>{content}</TableCellFocusContext.Provider>;
        };
      }
      const fallbackMinWidth = column.flex ? defaultFlexibleColumnMinWidth : defaultColumnMinWidth;
      const minWidth = column.minWidth ?? (autosizeColumns ? defaultAutosizeColumnMinWidth : column.width ?? fallbackMinWidth);
      const savedWidth = columnWidths[column.field];
      if (savedWidth !== undefined) {
        column.width = Math.max(minWidth, Math.min(column.maxWidth ?? Infinity, savedWidth));
        column.flex = undefined;
      }
      if (index >= resolvedStickyColumnCount) return { ...column, minWidth };

      const width = column.width ?? minWidth;
      const stickyMinWidth = autosizeColumns || index === resolvedStickyColumnCount - 1 ? minWidth : sourceColumn.width ?? minWidth;
      const className = `${stickyColumnClassPrefix}${index}`;
      styles[`& .${className}`] = {
        position: 'sticky',
        left: stickyLeft,
        zIndex: 2,
        ...(index === resolvedStickyColumnCount - 1 ? { borderRight: `1px solid ${designTokens.color.divider}` } : undefined),
        bgcolor: 'inherit',
      };
      styles[`& .MuiDataGrid-columnHeader.${className}`] = {
        zIndex: 4,
      };
      if (columnVisibilityModel?.[column.field] !== false) stickyLeft += width;
      return {
        ...column,
        flex: undefined,
        width,
        minWidth: stickyMinWidth,
        cellClassName: appendClassName(column.cellClassName, className),
        headerClassName: appendClassName(column.headerClassName, className),
      };
    });

    return { displayColumns: resolved, stickyColumnStyles: styles };
  }, [columns, resolvedStickyColumnCount, Boolean(selection), textLines, autosizeColumns, columnWidths, columnVisibilityModel]);

  useEffect(() => {
    setPaginationModel((current) => current.page < pageCount ? current : { ...current, page: Math.max(0, pageCount - 1) });
  }, [pageCount]);

  useEffect(() => {
    setPaginationModel((current) => current.page === 0 ? current : { ...current, page: 0 });
  }, [query, filterValueSignature]);

  return (
    // 검색·필터의 외곽 초점 표시는 열어 두고, 잘림·스크롤은 아래 표 viewport가 소유합니다.
    <Box sx={{ height: '100%', minHeight: 0, minWidth: 0, display: 'flex', flexDirection: 'column', gap: `${designTokens.layout.pageSectionGap}px` }}>
      <CommonFilterBar
        {...searchProps}
        filters={filters}
        trailing={(trailing != null || showCollectionMeta) ? (
          <>
            {trailing}
            {showTotalCount && <Stack aria-live="polite" aria-atomic="true" direction="row" spacing={1} useFlexGap sx={{ alignItems: 'baseline', flexWrap: 'wrap', minWidth: 0, ml: { xs: 'auto', sm: 0 } }}>
              <Typography sx={{ ...designTokens.typography.ui, fontWeight: 600, whiteSpace: 'nowrap', fontVariantNumeric: 'tabular-nums' }}>총 {totalCount.toLocaleString('ko-KR')}건</Typography>
              {hasNarrowedResults && <Typography variant="caption" color="text.secondary" sx={{ whiteSpace: 'nowrap', fontVariantNumeric: 'tabular-nums' }}>검색 결과 {rows.length.toLocaleString('ko-KR')}건</Typography>}
            </Stack>}
            {showPagination && <Stack direction="row" spacing={1} useFlexGap sx={{ alignItems: 'center', justifyContent: 'space-between', width: { xs: '100%', sm: 'auto' }, minWidth: 0 }}>
              <Select
                size="small"
                value={paginationModel.pageSize}
                inputProps={{ 'aria-label': '페이지당 행 수' }}
                SelectDisplayProps={{ style: { paddingLeft: designTokens.layout.buttonInlineInset } }}
                onChange={(event) => setPaginationModel({ page: 0, pageSize: Number(event.target.value) })}
                sx={{
                  minWidth: 104,
                  height: compact || coarsePointer ? designTokens.layout.touchTargetMinSize : designTokens.layout.pageHeaderControlSize,
                  borderRadius: designTokens.radius.button,
                  ...designTokens.typography.ui,
                  '& .MuiOutlinedInput-notchedOutline': { borderRadius: 'inherit' },
                }}
              >
                {pageSizeOptions.map((option) => <MenuItem key={option} value={option}>{option}개씩</MenuItem>)}
              </Select>
              {compact ? <Stack component="nav" aria-label="목록 페이지" direction="row" spacing={0.5} sx={{ alignItems: 'center' }}>
                <IconButton
                  aria-label="이전 페이지"
                  disabled={state.kind !== 'ready' || paginationModel.page === 0 || rows.length === 0}
                  onClick={() => setPaginationModel((current) => ({ ...current, page: Math.max(0, current.page - 1) }))}
                  sx={{ width: designTokens.layout.touchTargetMinSize, height: designTokens.layout.touchTargetMinSize, borderRadius: designTokens.radius.circle }}
                ><ChevronLeftOutlined /></IconButton>
                <Typography aria-live="polite" sx={{ ...designTokens.typography.ui, minWidth: 48, textAlign: 'center', fontVariantNumeric: 'tabular-nums', whiteSpace: 'nowrap' }}>
                  {paginationModel.page + 1} / {pageCount}
                </Typography>
                <IconButton
                  aria-label="다음 페이지"
                  disabled={state.kind !== 'ready' || paginationModel.page + 1 >= pageCount || rows.length === 0}
                  onClick={() => setPaginationModel((current) => ({ ...current, page: Math.min(pageCount - 1, current.page + 1) }))}
                  sx={{ width: designTokens.layout.touchTargetMinSize, height: designTokens.layout.touchTargetMinSize, borderRadius: designTokens.radius.circle }}
                ><ChevronRightOutlined /></IconButton>
              </Stack> : <><Pagination
                size="small"
                shape="circular"
                page={paginationModel.page + 1}
                count={pageCount}
                siblingCount={1}
                boundaryCount={1}
                disabled={state.kind !== 'ready' || rows.length === 0}
                onChange={(_event, page) => setPaginationModel((current) => ({ ...current, page: page - 1 }))}
                aria-label="목록 페이지"
              /><Box component="span" aria-live="polite" aria-atomic="true" sx={{ position: 'absolute', width: '1px', height: '1px', p: 0, m: '-1px', overflow: 'hidden', clip: 'rect(0 0 0 0)', whiteSpace: 'nowrap', border: 0 }}>
                {state.kind === 'ready' && rows.length > 0 ? `${paginationModel.page + 1} / ${pageCount} 페이지` : ''}
              </Box></>}
            </Stack>}
          </>
        ) : undefined}
      />
      <Box
        onFocusCapture={(event) => setGridHasFocus(event.currentTarget.contains(event.target))}
        onBlurCapture={(event) => {
          if (!event.currentTarget.contains(event.relatedTarget)) setGridHasFocus(false);
        }}
        sx={{
          flex: 1,
          minHeight: 0,
          overflow: 'hidden',
          border: embedded ? 0 : `1px solid ${designTokens.color.tableCellDivider}`,
          borderRadius: embedded ? 0 : designTokens.radius.table,
          bgcolor: designTokens.color.canvas,
        }}
      >
        <ResourceState
          embedded
          loading={state.kind === 'loading'}
          loadingLabel={state.kind === 'loading' ? state.label : undefined}
          loadingVariant="table"
          loadingColumns={displayColumns.filter((column) => columnVisibilityModel?.[column.field] !== false).length}
          loadingRowHeight={resolvedRowHeight}
          error={state.kind === 'error' ? state.message : undefined}
          errorTitle={state.kind === 'error' ? state.title : undefined}
          retryLabel={state.kind === 'error' ? state.retryLabel : undefined}
          onRetry={state.kind === 'error' ? state.onRetry : undefined}
          errorAction={state.kind === 'error' ? state.action : undefined}
          empty={state.kind === 'empty'}
          emptyTitle={state.kind === 'empty' ? state.title : undefined}
          emptyDescription={state.kind === 'empty' ? state.description : undefined}
          emptyAction={state.kind === 'empty' ? state.action : undefined}
        >
          <TableGridFocusContext.Provider value={gridHasFocus}><ResourceDataGrid
            initialAutosize={autosizeColumns && !initialAutosizeComplete}
            onInitialAutosize={completeInitialAutosize}
            onColumnWidthChange={({ colDef, width }) => setColumnWidths((current) => current[colDef.field] === width ? current : { ...current, [colDef.field]: width })}
            keepColumnsMounted={hasStickyColumns}
            stickyFields={[...(selection ? ['__check__'] : []), ...displayColumns.slice(0, resolvedStickyColumnCount).map((column) => column.field)]}
            rows={rows}
            aria-label={ariaLabel}
            columns={displayColumns}
            columnVisibilityModel={columnVisibilityModel}
            rowHeight={resolvedRowHeight}
            columnHeaderHeight={designTokens.layout.resourceHeaderHeight}
            disableColumnMenu
            checkboxSelection={Boolean(selection)}
            disableRowSelectionExcludeModel
            rowSelectionModel={selection ? { type: 'include', ids: new Set(selection.ids) } : undefined}
            onRowSelectionModelChange={selection ? (model) => selection.onChange([...model.ids]) : undefined}
            disableRowSelectionOnClick
            onRowClick={onRowClick}
            onCellKeyDown={(params, event) => {
              if (params.field === '__check__' || !onRowClick || (event.key !== 'Enter' && event.key !== ' ')) return;
              const target = event.target;
              if (target instanceof HTMLElement && target.closest('button, a, input, select, textarea, [role="button"], [role="link"]')) return;

              event.preventDefault();
              event.defaultMuiPrevented = true;
              onRowClick({ id: params.id, row: params.row as Row, columns: [...columns] });
            }}
            paginationModel={showAllRows ? { page: 0, pageSize: -1 } : paginationModel}
            onPaginationModelChange={showPagination ? setPaginationModel : undefined}
            sortModel={sortModel}
            onSortModelChange={setSortModel}
            pageSizeOptions={showAllRows ? [{ value: -1, label: '전체' }] : [...pageSizeOptions]}
            hideFooter
            sx={{
              height: '100%',
              border: 0,
              borderRadius: 'inherit',
              // 숨은 정렬 아이콘도 측정해 제목과 아이콘 사이 간격까지 폭에 포함합니다.
              '&.MuiDataGrid-autosizing .MuiDataGrid-iconButtonContainer, &.MuiDataGrid-autosizing .MuiDataGrid-columnHeaderTitleContainerContent': {
                flexShrink: 0,
              },
              '&.MuiDataGrid-autosizing .MuiDataGrid-iconButtonContainer': {
                width: 'auto',
              },
              '& .MuiDataGrid-columnHeaders .MuiDataGrid-columnHeader, & .MuiDataGrid-columnHeaders .MuiDataGrid-filler, & .MuiDataGrid-columnHeaders .MuiDataGrid-scrollbarFiller': {
                borderBottomColor: designTokens.color.tableCellDivider,
              },
              '& .MuiDataGrid-columnHeader[aria-colindex="1"]:focus, & .MuiDataGrid-columnHeader[aria-colindex="1"]:focus-within': {
                borderTopLeftRadius: embedded ? 0 : designTokens.radius.table,
              },
              '& .MuiDataGrid-columnSeparator': {
                opacity: 0,
              },
              '& .MuiDataGrid-columnSeparator--resizable:hover, & .MuiDataGrid-columnSeparator--resizing': {
                opacity: 1,
                color: designTokens.color.action,
              },
              // 고정 셀도 행의 선택·hover 배경을 공유하고 스크롤 뒤쪽 내용은 가립니다.
              '& .MuiDataGrid-row': { cursor: onRowClick ? 'pointer' : 'default' },
              ...stickyColumnStyles,
              // 같은 체크박스 선택자를 사용하는 고정 스타일을 병합한 뒤 폭 예외를 적용합니다.
              '& .MuiDataGrid-cellCheckbox, & .MuiDataGrid-columnHeaderCheckbox': {
                ...stickyColumnStyles['& .MuiDataGrid-cellCheckbox, & .MuiDataGrid-columnHeaderCheckbox'],
                paddingInline: 0,
              },
              [`& .${actionColumnClassName}`]: { paddingInline: 1 },
              [`& .${numericColumnClassName}`]: { fontVariantNumeric: 'tabular-nums' },
            }}
          /></TableGridFocusContext.Provider>
        </ResourceState>
      </Box>
    </Box>
  );
}
